import { css, type Theme } from "@emotion/react";
import styled from "@emotion/styled";

export const Container = styled.label`
    display: block;
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[1]};

    ${({ theme }) => theme.typography["Body/Regular/Text3"]}
`;

export const StyledLabel = styled.p`
    margin: 0;
    // These lines are needed to display tooltip content on top of input ⤵
    position: relative;
    z-index: 1;
`;

export const StyledHelperText = styled.p<{ hasError: boolean }>`
    ${({ theme }) => theme.typography["Caption/Regular/Caption1"]}
    margin: 0;

    ${({ hasError, theme }) =>
        hasError
            ? css`
                  color: ${theme.colors.destructive[400]};
              `
            : ""}
`;

export const Precision = styled.span`
    color: ${({ theme }) => theme.colors.neutral[500]};
`;

export const InputContainer = styled.span`
    position: relative;
    display: inherit;
    margin: ${({ theme }) => theme.spacing[1]} 0;
`;

const PlaceholderElement = styled.span`
    position: absolute;
    top: ${({ theme }) => theme.spacing[1]};
    width: 24px;
    height: 24px;

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
`;

export const ElementLeft = styled(PlaceholderElement)`
    left: ${({ theme }) => theme.spacing[1]};
`;
export const ElementRight = styled(PlaceholderElement)`
    right: ${({ theme }) => theme.spacing[1]};
`;

interface StyledInputProps {
    hasLeftElement: boolean;
    hasRightElement: boolean;
    hasError: boolean;
    isFocused: boolean;
}

const getInputStyle = ({ hasLeftElement, hasRightElement, hasError, isFocused }: StyledInputProps, theme: Theme) => {
    return `
    display: flex;
    box-sizing: border-box;
    width: 264px;
    height: 40px;
    margin: 0;
    padding: ${theme.spacing[1]} ${theme.spacing[2]};
    align-items: center;
    gap: ${theme.spacing[1]};

    border-radius: ${theme.spacing[1]};
    border: 1px solid ${theme.colors.neutral[200]};
    box-shadow: ${theme.shadow.outside[100]};

    ${
        hasLeftElement
            ? `
                  padding-left: ${theme.spacing[4]};
                  width: 240px;
              `
            : ""
    }
    ${
        hasRightElement
            ? `
                  padding-right: ${theme.spacing[4]};
                  width: 240px;
              `
            : ""
    }
    ${
        hasError
            ? `
                  border: 1px solid ${theme.colors.destructive[400]};
              `
            : ""
    }
    ${
        isFocused
            ? `
                  border: 1px solid ${theme.colors.primary[500]};
                  outline: 1px solid ${theme.colors.primary[500]};
              `
            : ""
    }

    &:hover {
        border: 1px solid ${theme.colors.primary[400]};

        &:disabled {
            border: 1px solid ${theme.colors.neutral[200]};
        }
    }

    &:focus {
        border: 1px solid ${theme.colors.primary[500]};
        outline: 1px solid ${theme.colors.primary[500]};
    }
    `;
};

export const CustomInputContainer = styled.div<StyledInputProps>`
    ${({ hasLeftElement, hasRightElement, hasError, isFocused, theme }) =>
        getInputStyle({ hasLeftElement, hasRightElement, hasError, isFocused }, theme)}
`;

export const StyledInput = styled.input<StyledInputProps>`
    ${({ hasLeftElement, hasRightElement, hasError, isFocused, theme }) =>
        getInputStyle({ hasLeftElement, hasRightElement, hasError, isFocused }, theme)}
`;
